// SPDX-License-Identifier: BUSL-1.1

/**
 * TimelinePage — full unified activity timeline.
 *
 * Path: /timeline
 * Auth: sessionAuth via RequireAuth in app.tsx
 *
 * Shows all GitHub + agent + governance activity for the current org.
 * Supports filtering by type, time range, and repo. Entries are expandable
 * to show full metadata.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Bot, GitFork, Settings, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { apiFetch } from '@/lib/api-client';
import { useCurrentOrg } from '@/contexts/org-context';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { TimelineFilterType } from '@/components/unified-timeline-card';

interface TimelineEntry {
  id: string;
  type: 'github' | 'agent_activity';
  timestamp: string;
  source: string;
  title: string;
  metadata: Record<string, unknown>;
}

interface TimelineResponse {
  timeline: TimelineEntry[];
  count: number;
}

const FILTER_OPTIONS: Array<{ value: TimelineFilterType; label: string }> = [
  { value: 'all', label: 'All activity' },
  { value: 'github', label: 'GitHub' },
  { value: 'agent', label: 'Agent' },
  { value: 'governance', label: 'Governance' },
];

const TIME_OPTIONS = [
  { value: '1', label: 'Last hour' },
  { value: '24', label: 'Last 24 hours' },
  { value: '168', label: 'Last 7 days' },
  { value: '720', label: 'Last 30 days' },
];

export function TimelinePage() {
  const { currentOrg } = useCurrentOrg();
  const slug = currentOrg?.slug ?? '';

  const [filter, setFilter] = useState<TimelineFilterType>('all');
  const [hours, setHours] = useState('24');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['unified-timeline-page', slug, filter, hours, page],
    queryFn: () =>
      apiFetch<TimelineResponse>(
        `/orgs/${slug}/timeline?hours=${hours}&limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`,
      ),
    enabled: !!slug,
    staleTime: 15_000,
    retry: 1,
  });

  const entries = data?.timeline ?? [];

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-100">Unified Timeline</h1>
        <p className="mt-1 text-sm text-neutral-400">
          {currentOrg
            ? `${currentOrg.name} — GitHub, agent, and governance activity`
            : 'All activity'}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Type filter */}
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Button variant="outline" size="sm" className="gap-1">
              {FILTER_OPTIONS.find((f) => f.value === filter)?.label ?? 'All activity'}
              <ChevronDown className="size-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {FILTER_OPTIONS.map((f) => (
              <DropdownMenuItem
                key={f.value}
                onClick={() => {
                  setFilter(f.value);
                  setPage(0);
                }}
                className={cn(filter === f.value && 'bg-neutral-800')}
              >
                {f.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Time range */}
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Button variant="outline" size="sm" className="gap-1">
              {TIME_OPTIONS.find((t) => t.value === hours)?.label ?? 'Last 24 hours'}
              <ChevronDown className="size-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {TIME_OPTIONS.map((t) => (
              <DropdownMenuItem
                key={t.value}
                onClick={() => {
                  setHours(t.value);
                  setPage(0);
                }}
                className={cn(hours === t.value && 'bg-neutral-800')}
              >
                {t.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Result count */}
        {data && (
          <span className="text-xs text-neutral-500">
            {data.count} event{data.count === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {/* Timeline */}
      <Card>
        <CardContent className="p-0">
          {isLoading && (
            <div className="flex flex-col divide-y divide-neutral-800">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-start gap-3 px-6 py-4">
                  <Skeleton className="h-4 w-12" />
                  <Skeleton className="size-5 rounded-full" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading && entries.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-16 text-center">
              <GitFork className="size-8 text-neutral-700" />
              <p className="text-sm text-neutral-500">No activity found for this time range.</p>
              <Link to="/github-settings" className="mt-2 text-xs text-blue-400 hover:underline">
                Connect GitHub to see activity
              </Link>
            </div>
          )}

          {!isLoading && entries.length > 0 && (
            <ul className="divide-y divide-neutral-800">
              {entries.map((entry) => (
                <ExpandableTimelineItem key={entry.id} entry={entry} />
              ))}
            </ul>
          )}

          {/* Pagination */}
          {data && data.count > PAGE_SIZE && (
            <div className="flex items-center justify-between border-t border-neutral-800 px-6 py-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || isFetching}
              >
                Previous
              </Button>
              <span className="text-xs text-neutral-500">
                Page {page + 1} · {entries.length} of {data.count}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={entries.length < PAGE_SIZE || isFetching}
              >
                {isFetching ? <Loader2 className="size-3 animate-spin" /> : null}
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ExpandableTimelineItem({ entry }: { entry: TimelineEntry }): React.ReactElement {
  const [expanded, setExpanded] = useState(false);

  const sourceConfig = {
    github: {
      icon: GitFork,
      color: 'text-purple-400',
      badge: 'bg-purple-900/40 text-purple-300',
      label: 'GitHub',
    },
    agent_activity: {
      icon: Bot,
      color: 'text-green-400',
      badge: 'bg-green-900/40 text-green-300',
      label: 'Agent',
    },
  } as const;

  const config = sourceConfig[entry.type] ?? sourceConfig.github;
  const Icon = config.icon;
  const timeLabel = relativeTime(entry.timestamp);
  const hasMeta = entry.metadata && Object.keys(entry.metadata).length > 0;

  return (
    <li>
      <button
        className="flex w-full items-start gap-3 px-6 py-4 text-left transition hover:bg-neutral-900/50"
        onClick={() => hasMeta && setExpanded((e) => !e)}
      >
        <time className="w-14 shrink-0 pt-0.5 font-mono text-xs text-neutral-500">{timeLabel}</time>

        <span
          className={cn(
            'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full',
            config.badge,
          )}
        >
          <Icon className={cn('size-3', config.color)} />
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-sm text-neutral-300">{entry.title}</p>
          <p className="mt-0.5 text-xs text-neutral-600">
            {config.label} · {entry.source}
          </p>
        </div>

        {hasMeta && (
          <ChevronRight
            className={cn(
              'mt-1 size-4 shrink-0 text-neutral-600 transition-transform',
              expanded && 'rotate-90',
            )}
          />
        )}
      </button>

      {/* Expanded metadata */}
      {expanded && hasMeta && (
        <div className="mx-6 mb-4 rounded border border-neutral-800 bg-neutral-950 p-3">
          <p className="mb-2 text-xs font-semibold tracking-wider text-neutral-600 uppercase">
            Event details
          </p>
          <pre className="overflow-x-auto text-xs text-neutral-400">
            {JSON.stringify(entry.metadata, null, 2)}
          </pre>
        </div>
      )}
    </li>
  );
}
